import { Row } from 'components/GridItemList';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useGetEmployeeWebQuery } from 'store/api/employeeWeb';
import { Employee } from 'types/employees';
import { t } from 'utils/getTranslations';
import { formatEmployeeName } from 'utils/nameOrder';
import { WebPictureViewer } from './WebPictureInput';

export const WEB_TITLE_FIELDS = [
    ['titleEng', 'employees.web.title-english'],
    ['titleEst', 'employees.web.title-estonian'],
    ['titleLav', 'employees.web.title-latvian'],
    ['titleLit', 'employees.web.title-lithuanian'],
] as const;

/**
 * The Web integration card's rows, in its form's order: the picture and the flags it edits, then what the website
 * receives with them, which comes from elsewhere and only reads. `null` while the employee has no web profile.
 */
export function useWebIntegrationRows(employee: Employee, lastNameFirst: boolean): Row[] | null {
    const web = employee.web;
    // The employee record carries no picture: it is the web profile's.
    const { data: detail } = useGetEmployeeWebQuery(String(employee.id), { skip: !web });
    if (!web) {
        return null;
    }
    const yesNo = (value: boolean) => t(value ? 'yes' : 'no');
    return [
        {
            label: t('employees.web.picture'),
            content: (
                <WebPictureViewer
                    pictureBase64={detail?.pictureBase64 ?? null}
                    fullName={formatEmployeeName(employee, lastNameFirst)}
                />
            ),
        },
        { label: t('employees.web.publicize-person'), content: yesNo(web.publicizePerson) },
        { label: t('employees.web.publicize-mobile'), content: yesNo(web.publicizeMobile) },
        { label: t('employees.web.on-leave'), content: yesNo(web.onLeave) },
        { label: t('employees.web.is-business-service'), content: yesNo(web.isBusinessService), readOnly: true },
        ...WEB_TITLE_FIELDS.map(([name, label]) => ({ label: t(label), content: web[name] || '-', readOnly: true })),
        {
            label: t('employees.web.last-published'),
            content: <FormattedDate value={web.lastPublishedAt} withTime />,
            readOnly: true,
        },
    ];
}
